import {
  ClubType,
  ClubDistance,
  UserProfile,
  ShotSituation,
  ShotAdvice,
  CourseConditions,
} from "../types";

// Standard club lofts and typical distance gaps
export const CLUB_DATA: Record<ClubType, { loft: number; order: number }> = {
  driver: { loft: 10.5, order: 1 },
  "3-wood": { loft: 15, order: 2 },
  "5-wood": { loft: 18, order: 3 },
  "7-wood": { loft: 21, order: 4 },
  "2-hybrid": { loft: 17, order: 5 },
  "3-hybrid": { loft: 19, order: 6 },
  "4-hybrid": { loft: 22, order: 7 },
  "5-hybrid": { loft: 25, order: 8 },
  "3-iron": { loft: 21, order: 9 },
  "4-iron": { loft: 24, order: 10 },
  "5-iron": { loft: 27, order: 11 },
  "6-iron": { loft: 30, order: 12 },
  "7-iron": { loft: 34, order: 13 },
  "8-iron": { loft: 38, order: 14 },
  "9-iron": { loft: 42, order: 15 },
  pw: { loft: 46, order: 16 },
  aw: { loft: 50, order: 17 },
  gw: { loft: 52, order: 18 },
  sw: { loft: 56, order: 19 },
  lw: { loft: 60, order: 20 },
  putter: { loft: 3, order: 21 },
};

export const calculateClubDistances = (
  referenceClubs: ClubDistance[],
): ClubDistance[] => {
  if (referenceClubs.length === 0) return [];

  const allClubs: ClubDistance[] = [];
  const sortedRefs = referenceClubs.sort(
    (a, b) => CLUB_DATA[a.club].order - CLUB_DATA[b.club].order,
  );

  // For each club type, calculate distance
  Object.keys(CLUB_DATA).forEach((clubType) => {
    const club = clubType as ClubType;
    const existing = referenceClubs.find((ref) => ref.club === club);

    if (existing) {
      allClubs.push(existing);
    } else {
      const distance = interpolateDistance(club, sortedRefs);
      allClubs.push({
        club,
        distance,
        isManuallySet: false,
      });
    }
  });

  return allClubs.sort(
    (a, b) => CLUB_DATA[a.club].order - CLUB_DATA[b.club].order,
  );
};

/**
 * Recalculate all club distances from known reference distances.
 * This is a fallback when AI recalibration fails - uses interpolation math.
 * 
 * @param knownDistances - Object mapping club types to known distances (e.g., { "7-iron": 150, "driver": 250 })
 * @returns Object mapping all club types to calculated distances
 */
export const recalculateFromKnownDistances = (
  knownDistances: { [club: string]: number }
): { [club: string]: number } => {
  // Convert known distances to ClubDistance format
  const referenceClubs: ClubDistance[] = Object.entries(knownDistances)
    .filter(([club, distance]) => {
      // Only include valid club types that exist in CLUB_DATA
      return CLUB_DATA[club as ClubType] !== undefined && distance > 0;
    })
    .map(([club, distance]) => ({
      club: club as ClubType,
      distance,
      isManuallySet: true,
    }));

  if (referenceClubs.length === 0) {
    throw new Error("No valid reference distances provided");
  }

  // Use existing interpolation logic
  const allDistances = calculateClubDistances(referenceClubs);

  // Return as object for easy mapping
  return allDistances.reduce((acc, { club, distance }) => {
    acc[club] = distance;
    return acc;
  }, {} as { [club: string]: number });
};

const interpolateDistance = (
  targetClub: ClubType,
  referenceClubs: ClubDistance[],
): number => {
  const targetOrder = CLUB_DATA[targetClub].order;

  // Find the closest reference clubs above and below
  let lowerRef: ClubDistance | null = null;
  let upperRef: ClubDistance | null = null;

  for (const ref of referenceClubs) {
    const refOrder = CLUB_DATA[ref.club].order;
    if (
      refOrder < targetOrder &&
      (!lowerRef || refOrder > CLUB_DATA[lowerRef.club].order)
    ) {
      lowerRef = ref;
    }
    if (
      refOrder > targetOrder &&
      (!upperRef || refOrder < CLUB_DATA[upperRef.club].order)
    ) {
      upperRef = ref;
    }
  }

  if (lowerRef && upperRef) {
    // Interpolate between the two references
    const lowerOrder = CLUB_DATA[lowerRef.club].order;
    const upperOrder = CLUB_DATA[upperRef.club].order;
    const ratio = (targetOrder - lowerOrder) / (upperOrder - lowerOrder);
    return Math.round(
      lowerRef.distance - (lowerRef.distance - upperRef.distance) * ratio,
    );
  } else if (lowerRef) {
    // Extrapolate from lower reference
    const gapPerClub = 12; // Average distance gap
    return Math.round(
      lowerRef.distance -
        (targetOrder - CLUB_DATA[lowerRef.club].order) * gapPerClub,
    );
  } else if (upperRef) {
    // Extrapolate from upper reference
    const gapPerClub = 12;
    return Math.round(
      upperRef.distance +
        (CLUB_DATA[upperRef.club].order - targetOrder) * gapPerClub,
    );
  }

  // Fallback to standard distances based on 7-iron = 150 yards
  const baseDistance = 150;
  const baseOrder = CLUB_DATA["7-iron"].order;
  const gapPerClub = 12;
  return Math.round(baseDistance + (baseOrder - targetOrder) * gapPerClub);
};

export const adjustDistanceForConditions = (
  baseDistance: number,
  conditions: CourseConditions,
): number => {
  let adjustedDistance = baseDistance;

  // Temperature adjustment (1 yard per 10 degrees from 70°F)
  const tempAdjustment = (conditions.temperature - 70) * 0.1;
  adjustedDistance += tempAdjustment;

  // Elevation adjustment (2% per 1000 feet)
  const elevationAdjustment =
    (conditions.elevation / 1000) * 0.02 * baseDistance;
  adjustedDistance += elevationAdjustment;

  // Wind adjustment (approximate)
  const windAdjustment = conditions.windSpeed * 0.5; // Simplified
  if (conditions.windDirection === "headwind") {
    adjustedDistance -= windAdjustment;
  } else if (conditions.windDirection === "tailwind") {
    adjustedDistance += windAdjustment;
  }

  // Firmness adjustment
  if (conditions.firmness === "firm") {
    adjustedDistance += baseDistance * 0.05;
  } else if (conditions.firmness === "soft") {
    adjustedDistance -= baseDistance * 0.05;
  }

  return Math.round(adjustedDistance);
};

export const generateShotAdvice = (
  situation: ShotSituation,
  userProfile: UserProfile | null,
): ShotAdvice => {
  const distances = userProfile?.clubDistances || [];

  // Find the best club for the distance
  const targetDistance = situation.distance;
  let recommendedClub: ClubType = "7-iron";
  let bestDiff = Infinity;

  distances.forEach(({ club, distance }) => {
    const diff = Math.abs(distance - targetDistance);
    if (diff < bestDiff) {
      bestDiff = diff;
      recommendedClub = club;
    }
  });

  // Generate alternatives (one club up and down)
  const clubOrder = CLUB_DATA[recommendedClub].order;
  const alternativeClubs: ClubType[] = [];

  Object.entries(CLUB_DATA).forEach(([club, data]) => {
    if (Math.abs(data.order - clubOrder) === 1) {
      alternativeClubs.push(club as ClubType);
    }
  });

  // Generate situation-specific adjustments
  const adjustments = {
    clubSelection: getClubSelectionAdvice(situation),
    ballPosition: getBallPositionAdvice(situation),
    stance: getStanceAdvice(situation),
    swing: getSwingAdvice(situation),
    aim: getAimAdvice(situation),
  };

  const tips = generateSituationTips(situation);

  return {
    recommendedClub,
    alternativeClubs,
    adjustments,
    tips,
  };
};

const getClubSelectionAdvice = (situation: ShotSituation): string => {
  let advice = "";

  if (situation.lie === "rough") {
    advice += "Consider taking one more club due to rough lie. ";
  }
  if (situation.slope === "uphill") {
    advice += "Take one more club for uphill lie. ";
  } else if (situation.slope === "downhill") {
    advice += "Take one less club for downhill lie. ";
  }
  if (situation.weather === "windy") {
    advice += "Consider wind direction for club selection. ";
  }

  return advice || "Standard club selection for this distance.";
};

const getBallPositionAdvice = (situation: ShotSituation): string => {
  if (situation.lie === "rough") {
    return "Move ball slightly back in stance for cleaner contact.";
  }
  if (situation.slope === "uphill") {
    return "Move ball forward in stance to match slope.";
  }
  if (situation.slope === "downhill") {
    return "Move ball back in stance to match slope.";
  }
  return "Standard ball position for this club.";
};

const getStanceAdvice = (situation: ShotSituation): string => {
  if (situation.slope === "sidehill-above") {
    return "Stand closer to ball, flex knees more, expect draw.";
  }
  if (situation.slope === "sidehill-below") {
    return "Stand farther from ball, stay balanced, expect fade.";
  }
  if (situation.lie === "bunker") {
    return "Widen stance, dig feet in for stability.";
  }
  return "Standard stance width and posture.";
};

const getSwingAdvice = (situation: ShotSituation): string => {
  if (situation.lie === "rough") {
    return "Make steeper swing, hit down on ball more.";
  }
  if (situation.lie === "bunker") {
    return "Open clubface, hit sand behind ball, follow through.";
  }
  if (situation.weather === "windy") {
    return "Make three-quarter swing for better control.";
  }
  return "Standard swing tempo and path.";
};

const getAimAdvice = (situation: ShotSituation): string => {
  if (situation.slope === "sidehill-above") {
    return "Aim right of target, ball will draw.";
  }
  if (situation.slope === "sidehill-below") {
    return "Aim left of target, ball will fade.";
  }
  if (situation.weather === "windy") {
    return "Aim into wind, allow for drift.";
  }
  return "Aim directly at target.";
};

const generateSituationTips = (situation: ShotSituation): string[] => {
  const tips: string[] = [];

  if (situation.lie === "rough") {
    tips.push("Take a practice swing to feel the grass resistance");
    tips.push("Grip down slightly for better control");
  }

  if (situation.lie === "bunker") {
    tips.push("Keep lower body quiet during swing");
    tips.push("Accelerate through impact");
  }

  if (situation.weather === "wet") {
    tips.push("Take one more club as ball won't roll as far");
    tips.push("Keep grip pressure light but secure");
  }

  if (situation.pin === "front") {
    tips.push("Favor center of green to avoid short-siding yourself");
  } else if (situation.pin === "back") {
    tips.push("Aggressive pin hunting acceptable here");
  }

  return tips;
};
